import {
  applyOperation,
  formatEquation,
  normalizeOperation,
  substitute,
  type Equation,
  type AlgebraOperation,
} from './algebra.ts';

/** Algebra is validated before turning it into reusable, cacheable narration. */
export const speakMath = (text: string) =>
  text
    .replace(/(\d+(?:\/\d+)?)x/g, '$1 times x')
    .replace(/=/g, ' equals ')
    .replace(/\+/g, ' plus ')
    .replace(/[-−]/g, ' minus ')
    .replace(/\//g, ' over ')
    .replace(/\s+/g, ' ')
    .trim();
export function operationNarration(
  before: Equation,
  input: AlgebraOperation,
): string {
  const operation = normalizeOperation(input);
  const after = applyOperation(before, operation);
  const value = speakMath(operation.value);
  const move =
    operation.kind === 'subtract'
      ? `Subtract ${value} from both entire sides`
      : operation.kind === 'add'
        ? `Add ${value} to both entire sides`
        : operation.kind === 'divide'
          ? `Divide both entire sides by ${value}`
          : `Multiply both entire sides by ${value}`;
  return `That move keeps the equation balanced. Start with ${speakMath(formatEquation(before))}. ${move}. Now we have ${speakMath(formatEquation(after))}. The solution is preserved. What could you do next?`;
}
export function candidateNarration(original: Equation, value: string): string {
  const check = substitute(original, value);
  return `${check.equal ? 'Yes, that value checks.' : 'Let us check that value together.'} Start with ${speakMath(formatEquation(original))}. Replace every x with ${speakMath(value)}. Multiply and add on each side. The left side is ${speakMath(check.left)}. The right side is ${speakMath(check.right)}. ${check.equal ? 'Both sides are equal, so this value is a solution. Explain your reasoning.' : 'The sides are not equal, so this value does not solve the original equation. Revisit your steps.'}`;
}

const small: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  fifteen: 15,
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
  sixty: 60,
  ninety: 90,
};
export function thinkingTimeRequest(text: string): number | undefined {
  const clean = text
    .toLowerCase()
    .trim()
    .replace(/[.!?]+$/, '');
  if (
    /^(?:please )?(?:i need|give me|let me have)(?: a little| some)? more time(?: to think)?(?: please)?$/.test(
      clean,
    )
  )
    return 120000;
  const match = clean.match(
    /^(?:please )?(?:give me|i need|wait|check (?:back|in) (?:in|after)|leave me for) (\d+|one|two|three|four|five|six|seven|eight|nine|ten|fifteen|twenty|thirty|forty|fifty|sixty|ninety) (seconds?|minutes?)(?: to think)?(?: please)?$/,
  );
  if (!match) return;
  const n = small[match[1]] ?? Number(match[1]);
  if (!Number.isFinite(n) || n <= 0) return;
  return Math.min(3600000, n * (match[2].startsWith('minute') ? 60000 : 1000));
}
export function shouldOfferHelp(s: {
  now: number;
  lastActivity: number;
  snoozedUntil: number;
  interval: number;
  enabled: boolean;
  available: boolean;
  occupied: boolean;
  visible: boolean;
  offered: boolean;
}) {
  return (
    s.enabled &&
    s.available &&
    !s.occupied &&
    s.visible &&
    !s.offered &&
    s.now >= s.snoozedUntil &&
    s.now - s.lastActivity >= s.interval
  );
}
